import { C, RADIUS } from '@parsina/framework/tokens';
import { LTR_TEXT } from '../../i18n';

/**
 * A share-of-total breakdown, as labelled bars.
 *
 * The desktop draws these as a donut. A donut on a phone is the wrong shape
 * twice over: at 320px the slices are too thin to distinguish and the legend
 * costs as much height as the chart, and the kit has no donut — drawing one
 * here would mean the hand-rolled SVG the redesign is removing, not adding.
 *
 * A horizontal bar answers the same question better on a narrow screen: the
 * labels read down the start edge in one column, the values line up in another,
 * and comparing two rows is a straight-line comparison rather than an angle
 * judgement.
 *
 * The bars are `aria-hidden`; the label and the value beside each one ARE the
 * accessible content, so nothing is announced twice and nothing is announced
 * only as a picture.
 *
 * Values arrive pre-formatted and already share-of-total — this component does
 * no arithmetic, so it cannot divide by a total that turned out to be zero.
 */
export default function MobileDistributionList({
  /** `[{ key, label, valueText, percent, color }]` — `percent` is 0–100. */
  items = [],
  emptyLabel,
  'data-testid': testId,
}) {
  if (!items.length) {
    return (
      <p data-testid={testId} data-state="empty" style={{ margin: 0, fontSize: '0.75rem', color: C.textMuted }}>
        {emptyLabel}
      </p>
    );
  }

  return (
    <ul
      data-testid={testId}
      data-state="content"
      style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.75rem' }}
    >
      {items.map((d) => (
        <li key={d.key} data-testid={testId ? `${testId}-${d.key}` : undefined}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.3125rem' }}>
            <span dir="auto" style={{ fontSize: '0.8125rem', fontWeight: 600, color: C.textSecondary, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.label}
            </span>
            <span {...LTR_TEXT} style={{ flexShrink: 0, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
              {d.valueText}
            </span>
          </div>
          <div
            aria-hidden="true"
            style={{ height: '0.5rem', background: C.bg, borderRadius: RADIUS.chip, overflow: 'hidden' }}
          >
            <div style={{
              height: '100%',
              width: `${Math.max(0, Math.min(100, d.percent || 0))}%`,
              background: d.color || C.navy,
              borderRadius: 'inherit',
            }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
